import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type {
  MedicalTimeline,
  TimelineAnalyte,
  TimelineEvent,
} from '../../api/documents';
import type { MemberTabId } from './MemberTabs';

interface MemberHealthSnapshotProps {
  timeline: MedicalTimeline | null;
  onSwitchTab: (tab: MemberTabId) => void;
}

function statusClass(status: string | null | undefined): string {
  if (!status) return 'ov-snapshot-status-mute';
  const s = status.toLowerCase();
  if (s.includes('critic')) return 'ov-snapshot-status-crit';
  if (s.includes('ridic') || s.includes('crescu') || s.includes('high')) return 'ov-snapshot-status-high';
  if (s.includes('scăz') || s.includes('scaz') || s.includes('low')) return 'ov-snapshot-status-low';
  if (s.includes('normal') || s.includes('ok')) return 'ov-snapshot-status-normal';
  return 'ov-snapshot-status-mute';
}

function formatRoDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' });
}

const VialIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="ov-empty-icon" aria-hidden>
    <path d="M9 2v6L4.2 17.5A2 2 0 0 0 6 20h12a2 2 0 0 0 1.8-2.5L15 8V2" />
    <path d="M8 2h8" />
    <path d="M7 14h10" />
  </svg>
);

const MemberHealthSnapshot: React.FC<MemberHealthSnapshotProps> = ({ timeline, onSwitchTab }) => {
  const latest = useMemo<{ event: TimelineEvent; analytes: TimelineAnalyte[] } | null>(() => {
    const events = timeline?.events ?? [];
    let best: TimelineEvent | null = null;
    let bestT = -Infinity;
    for (const ev of events) {
      if (!ev.analytes || ev.analytes.length === 0) continue;
      const t = new Date(ev.date).getTime();
      if (Number.isNaN(t)) continue;
      if (t > bestT) {
        bestT = t;
        best = ev;
      }
    }
    if (!best) return null;
    return { event: best, analytes: best.analytes.slice(0, 6) };
  }, [timeline]);

  if (!latest) {
    return (
      <div className="ov-empty" role="status">
        <VialIcon />
        <div className="ov-empty-title">Niciun rezultat de laborator</div>
        <div className="ov-empty-text">
          {timeline
            ? 'Cronologia nu conține analize cu valori măsurate.'
            : 'Generează cronologia pentru a vedea snapshot-ul rezultatelor recente.'}
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onSwitchTab('timeline')}
        >
          Deschide cronologia
        </button>
      </div>
    );
  }

  const { event, analytes } = latest;
  const sourceDocId = event.source_document_ids[0];

  return (
    <>
      <div className="ov-snapshot-meta">
        <span><strong style={{ color: 'var(--color-slate-700)' }}>{event.title}</strong></span>
        <span>{formatRoDate(event.date)}</span>
        {event.specialty && <span>{event.specialty}</span>}
      </div>
      <table className="ov-snapshot-table">
        <thead>
          <tr>
            <th>Analit</th>
            <th>Valoare</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {analytes.map((a, i) => (
            <tr key={`${a.name}-${i}`}>
              <td>{a.name}</td>
              <td>
                {a.value ?? '—'}
                {a.unit ? ` ${a.unit}` : ''}
              </td>
              <td>
                <span className={`ov-snapshot-status ${statusClass(a.status)}`}>
                  {a.status || '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sourceDocId !== undefined && (
        <div style={{ marginTop: 'var(--sp-3)', textAlign: 'right' }}>
          <Link
            to={`/documents/${sourceDocId}`}
            style={{ fontSize: 'var(--text-xs)', fontWeight: 600 }}
          >
            Vezi document →
          </Link>
        </div>
      )}
    </>
  );
};

export default MemberHealthSnapshot;
